import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeInput, setActiveInput] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [cooldownMs, setCooldownMs] = useState(0);
  const intervalRef = useRef(null);

  const COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes
  const getStorageKey = (addr) =>
    `pwdResetCooldown:${(addr || "").toLowerCase().trim()}`;
  const clearIntervalIfAny = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  const startCooldownTimer = (lastSentAt) => {
    clearIntervalIfAny();
    const computeRemaining = () => {
      const remaining = Math.max(0, lastSentAt + COOLDOWN_MS - Date.now());
      setCooldownMs(remaining);
      if (remaining <= 0) {
        clearIntervalIfAny();
      }
    };
    computeRemaining();
    if (lastSentAt + COOLDOWN_MS > Date.now()) {
      intervalRef.current = setInterval(computeRemaining, 1000);
    }
  };
  const syncCooldownForEmail = (addr) => {
    const key = getStorageKey(addr);
    const stored = localStorage.getItem(key);
    if (stored) {
      const lastSentAt = parseInt(stored, 10);
      if (!Number.isNaN(lastSentAt)) {
        startCooldownTimer(lastSentAt);
        return;
      }
    }
    setCooldownMs(0);
    clearIntervalIfAny();
  };

  useEffect(() => {
    const newShapes = [];
    for (let i = 0; i < 15; i++) {
      newShapes.push({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: 20 + Math.random() * 50,
        duration: 15 + Math.random() * 30,
        delay: Math.random() * 5,
      });
    }
    setShapes(newShapes);
  }, []);

  // Recompute cooldown when email changes
  useEffect(() => {
    syncCooldownForEmail(email);
    return () => clearIntervalIfAny();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const normalizeFirebaseMessage = (err) => {
    const code = err?.code || "";
    if (code === "auth/invalid-email") return "Enter a valid email address.";
    if (code === "auth/too-many-requests")
      return "Too many attempts. Please try again later.";
    return "Unable to send reset email. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const trimmedEmail = (email || "").trim();
    if (!trimmedEmail) return;
    if (cooldownMs > 0) return;
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
      };
      await sendPasswordResetEmail(auth, trimmedEmail, actionCodeSettings);

      // For security, show generic success even if user doesn't exist
      setSuccess(
        "If an account exists for this email, a reset link has been sent. (Please check your spam folder as well)"
      );

      // persist cooldown for this email
      const now = Date.now();
      localStorage.setItem(getStorageKey(trimmedEmail), String(now));
      startCooldownTimer(now);
    } catch (err) {
      // user-not-found should also show generic success to avoid enumeration
      if (err?.code === "auth/user-not-found") {
        setSuccess(
          "If an account exists for this email, a reset link has been sent. (Please check your spam folder as well)"
        );
        const now = Date.now();
        localStorage.setItem(getStorageKey((email || "").trim()), String(now));
        startCooldownTimer(now);
      } else if (err?.code === "auth/too-many-requests") {
        // Respect server-side throttling and also apply our client cooldown
        setError("Too many attempts. Please try again later.");
        const now = Date.now();
        localStorage.setItem(getStorageKey((email || "").trim()), String(now));
        startCooldownTimer(now);
      } else {
        setError(normalizeFirebaseMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatMmSs = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const m = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(1, "0");
    const s = (totalSeconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 py-12 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {shapes.map((shape) => (
        <div
          key={shape.id}
          className="absolute rounded-full bg-white opacity-10 animate-float"
          style={{
            top: `${shape.top}%`,
            left: `${shape.left}%`,
            width: `${shape.size}px`,
            height: `${shape.size}px`,
            animationDuration: `${shape.duration}s`,
            animationDelay: `${shape.delay}s`,
          }}
        />
      ))}

      <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 opacity-30 blur-3xl -top-20 -right-20"></div>
      <div className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 opacity-20 blur-3xl bottom-10 -left-10"></div>

      <div className="max-w-md w-full space-y-8 backdrop-blur-lg bg-white bg-opacity-10 p-8 rounded-2xl shadow-2xl border border-white border-opacity-20 relative z-10 transition-all duration-500 hover:shadow-purple-500/20">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 blur opacity-70 animate-pulse"></div>
              <h1 className="relative bg-gradient-to-r from-indigo-400 to-pink-500 bg-clip-text text-transparent text-5xl font-extrabold">
                Q
              </h1>
            </div>
          </div>
          <h2 className="text-center text-2xl font-bold text-white">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-white text-opacity-80">
            Enter your email address and we'll send you a reset link.
          </p>
        </div>

        {error && (
          <div className="bg-red-900 bg-opacity-40 border-l-4 border-red-500 p-4 rounded-md backdrop-blur-sm">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-emerald-900 bg-opacity-40 border-l-4 border-emerald-500 p-4 rounded-md backdrop-blur-sm">
            <p className="text-sm text-emerald-200">{success}</p>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md space-y-4">
            <div className="relative">
              <label
                htmlFor="email"
                className={`block text-sm font-medium transition-all duration-300 ${
                  activeInput === "email" ? "text-pink-300" : "text-gray-200"
                }`}
              >
                Email address
              </label>
              <div
                className={`mt-1 relative transition-all duration-300 ${
                  activeInput === "email" ? "transform -translate-y-1" : ""
                }`}
              >
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="appearance-none rounded-lg block w-full px-3 py-3 border-2 bg-white bg-opacity-10 backdrop-blur-md placeholder-gray-400 text-white focus:ring-0 focus:bg-opacity-20 focus:border-pink-500 transition-all duration-300 sm:text-sm"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setActiveInput("email")}
                  onBlur={() => setActiveInput(null)}
                />
                <div
                  className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-500 ${
                    activeInput === "email" ? "w-full" : "w-0"
                  }`}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              className="text-sm text-pink-300 hover:text-pink-100 font-medium transition-colors duration-300"
              onClick={() => navigate("/login")}
            >
              ← Back to sign in
            </button>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !email || cooldownMs > 0}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
            >
              {loading
                ? "Sending..."
                : cooldownMs > 0
                ? `Resend in ${formatMmSs(cooldownMs)}`
                : success
                ? "Resend link"
                : "Send reset link"}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes float {
          0% {
            transform: translateY(0) translateX(0) rotate(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px) rotate(10deg);
          }
          100% {
            transform: translateY(0) translateX(0) rotate(0);
          }
        }
        .animate-float {
          animation-name: float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
